// Definitions for Boolean Expressions

export type BooleanExpression =
  | { type: 'TRUE' }
  | { type: 'FALSE' }
  | { type: 'CONTACT'; address: string; isNormallyClosed: boolean; elementId: string }
  | { type: 'NOT'; expr: BooleanExpression; elementId: string }
  | { type: 'EDGE_CONTACT'; address: string; edge: 'RISING' | 'FALLING'; elementId: string }
  | {
      type: 'COMPARE';
      op:
        | 'CMP_EQ_I'
        | 'CMP_NE_I'
        | 'CMP_GT_I'
        | 'CMP_LT_I'
        | 'CMP_GE_I'
        | 'CMP_LE_I'
        | 'CMP_EQ_R'
        | 'CMP_NE_R'
        | 'CMP_GT_R'
        | 'CMP_LT_R'
        | 'CMP_GE_R'
        | 'CMP_LE_R';
      in1: string;
      in2: string;
      elementId: string;
    }
  | { type: 'IN_RANGE'; in: string; min: string; max: string; elementId: string }
  | { type: 'AND'; left: BooleanExpression; right: BooleanExpression }
  | { type: 'OR'; left: BooleanExpression; right: BooleanExpression };

// Helper to simplify boolean expressions during construction
export function exprOr(a: BooleanExpression, b: BooleanExpression): BooleanExpression {
  if (a.type === 'FALSE') return b;
  if (b.type === 'FALSE') return a;
  if (a.type === 'TRUE' || b.type === 'TRUE') return { type: 'TRUE' };
  return { type: 'OR', left: a, right: b };
}

export function exprAnd(a: BooleanExpression, b: BooleanExpression): BooleanExpression {
  if (a.type === 'FALSE' || b.type === 'FALSE') return { type: 'FALSE' };
  if (a.type === 'TRUE') return b;
  if (b.type === 'TRUE') return a;
  return { type: 'AND', left: a, right: b };
}

import type { RuntimeHost } from '../../plc/runtime/RuntimeHost';
import { parseAddress, parseOperand } from '../../plc/address/parser';

export function evaluateExpression(expr: BooleanExpression, host: RuntimeHost): boolean {
  switch (expr.type) {
    case 'TRUE': return true;
    case 'FALSE': return false;
    case 'NOT':
      return !evaluateExpression(expr.expr, host);
    case 'CONTACT': {
      const addr = parseAddress(expr.address);
      if (!addr || (addr.size !== 'X' && addr.area !== 'T' && addr.area !== 'C')) return false;
      let val = false;
      switch (addr.area) {
        case 'I': val = host.pi.inputs.getBit(addr.byteOffset, addr.bitOffset); break;
        case 'Q': val = host.pi.outputs.getBit(addr.byteOffset, addr.bitOffset); break;
        case 'M': val = host.pi.memory.getBit(addr.byteOffset, addr.bitOffset); break;
        case 'T': val = host.timers.getTimer(`T${addr.byteOffset}`)?.output ?? false; break;
        case 'C': val = host.counters.getCounter(`C${addr.byteOffset}`)?.q ?? false; break;
      }
      return expr.isNormallyClosed ? !val : val;
    }
    case 'EDGE_CONTACT': {
      const addr = parseAddress(expr.address);
      if (!addr) return false;
      let val = false;
      if (addr.area === 'I') val = host.pi.inputs.getBit(addr.byteOffset, addr.bitOffset);
      else if (addr.area === 'Q') val = host.pi.outputs.getBit(addr.byteOffset, addr.bitOffset);
      else if (addr.area === 'M') val = host.pi.memory.getBit(addr.byteOffset, addr.bitOffset);
      return val;
    }
    case 'COMPARE': {
      const isReal = expr.op.endsWith('_R');
      const getVal = (inStr: string): number => {
        const op = parseOperand(inStr);
        if (!op) return 0;
        if (op.type === 'CONSTANT') return op.value;
        const addr = op.addr;
        if (isReal && addr.size === 'D') {
          if (addr.area === 'M') return host.pi.memory.getFloat(addr.byteOffset);
          if (addr.area === 'I') return host.pi.inputs.getFloat(addr.byteOffset);
          if (addr.area === 'Q') return host.pi.outputs.getFloat(addr.byteOffset);
        }
        if (addr.area === 'M') return host.pi.memory.getInt(addr.byteOffset);
        if (addr.area === 'I') return host.pi.inputs.getInt(addr.byteOffset);
        if (addr.area === 'Q') return host.pi.outputs.getInt(addr.byteOffset);
        if (addr.area === 'T') return host.timers.getTimer(`T${addr.byteOffset}`)?.elapsedMs ?? 0;
        return host.counters.getCounter(`C${addr.byteOffset}`)?.cv ?? 0;
      };
      const v1 = getVal(expr.in1);
      const v2 = getVal(expr.in2);
      switch (expr.op) {
        case 'CMP_EQ_I': return v1 === v2;
        case 'CMP_NE_I': return v1 !== v2;
        case 'CMP_GT_I': return v1 > v2;
        case 'CMP_LT_I': return v1 < v2;
        case 'CMP_GE_I': return v1 >= v2;
        case 'CMP_LE_I': return v1 <= v2;
        case 'CMP_EQ_R': return Math.abs(v1 - v2) < 0.00001;
        case 'CMP_NE_R': return Math.abs(v1 - v2) >= 0.00001;
        case 'CMP_GT_R': return v1 > v2;
        case 'CMP_LT_R': return v1 < v2;
        case 'CMP_GE_R': return v1 >= v2;
        case 'CMP_LE_R': return v1 <= v2;
        default: return false;
      }
    }
    case 'IN_RANGE': {
      const getVal = (inStr: string): number => {
        const op = parseOperand(inStr);
        if (!op) return 0;
        if (op.type === 'CONSTANT') return op.value;
        const addr = op.addr;
        if (addr.area === 'M') return host.pi.memory.getInt(addr.byteOffset);
        if (addr.area === 'I') return host.pi.inputs.getInt(addr.byteOffset);
        if (addr.area === 'Q') return host.pi.outputs.getInt(addr.byteOffset);
        return 0;
      };
      const v = getVal(expr.in);
      const min = getVal(expr.min);
      const max = getVal(expr.max);
      return v >= min && v <= max;
    }
    case 'AND': return evaluateExpression(expr.left, host) && evaluateExpression(expr.right, host);
    case 'OR': return evaluateExpression(expr.left, host) || evaluateExpression(expr.right, host);
  }
}
